import algoliasearch from "algoliasearch";
import { createNullCache } from "@algolia/cache-common";
import _ from "lodash";

import AWS from "aws-sdk";

import env from "../constants/env";
import state from "../state/state";

import { getOrgId } from "../actions/orgsActions";

import { getHistoryIndexName, getSearchIndexName } from "../actions/orgsActions";

import * as awsS3 from "../persistence/s3";
import { refreshCredentials } from "../utils/securityUtil";
import { mergeStatusSelectionData, normalizeStatusSelectionRefs } from "../utils/statusSelectionUtils";
import { showInfo, showError2 } from "../actions/alertActions";
import { hideStatusSpinner, showStatusSpinner } from "../actions/actions";

let client, client2;

const ALGOLIA_PROXY_BROWSE_PAGE_SIZE = 250;
const ALGOLIA_PROXY_GET_OBJECTS_CHUNK_SIZE = 100;

function getSearchApiKey() {
	return _.get(env, "algolia.searchApiKey") || _.get(env, "algolia.apiKey");
}

function encodeS3CopySourceKey(sourceKey = "") {
	return _.toString(sourceKey || "")
		.split("/")
		.map(part => encodeURIComponent(part))
		.join("/");
}

const HISTORY_REINDEX_CONCURRENCY = 5;

export function getClient() {
	if (!client) {
		client = algoliasearch(env.algolia.applicationId, getSearchApiKey(), {
			// optional: eliminate local caching completely
			responsesCache: createNullCache(),
			requestsCache: createNullCache(),
			queriesCache: createNullCache(),
		});
	}
	return client;
}

export function getClient2() {
	if (!client2) {
		client2 = algoliasearch(env.algolia.applicationId, getSearchApiKey(), {
			// optional: eliminate local caching completely
			responsesCache: createNullCache(),
			requestsCache: createNullCache(),
			queriesCache: createNullCache(),
		});
	}
	return client2;
}

// src/search/algolia.js

export function bustAlgoliaCaches(client) {
	try {
		const t = client && client.transporter;
		if (!t) { return; }

		if (t.responsesCache && typeof t.responsesCache.clear === "function") {
			t.responsesCache.clear();
		}
		if (t.requestsCache && typeof t.requestsCache.clear === "function") {
			t.requestsCache.clear();
		}
	} catch (e) {
		// optional: log but don't crash the app
		console.warn("Algolia cache clear failed:", e);
	}
}

function parseLambdaPayload(data) {
	if (!data || !data.Payload) {
		return null;
	}

	let payload = data.Payload;
	if (_.isString(payload)) {
		try {
			payload = JSON.parse(payload);
		} catch (_err) {
			return payload;
		}
	}

	if (_.isString(payload)) {
		try {
			payload = JSON.parse(payload);
		} catch (_err) {
			return payload;
		}
	}

	if (payload && (payload.errorMessage || payload.message) && payload.success === false) {
		throw new Error(payload.errorMessage || payload.message);
	}

	return payload;
}

export async function invokeAlgoliaProxy(operation, payload = {}) {
	await refreshCredentials();

	const lambda = new AWS.Lambda({
		region: _.get(env, "aws.defaultRegion", "us-east-1"),
		apiVersion: "2015-03-31"
	});

	const data = await lambda.invoke({
		FunctionName: env.lambda.searchIndexFunction,
		InvocationType: "RequestResponse",
		LogType: "None",
		Payload: JSON.stringify(_.assign({
			method: "algoliaProxy",
			operation,
			orgId: getOrgId()
		}, payload))
	}).promise();

	const parsedPayload = parseLambdaPayload(data);
	if (data && data.FunctionError) {
		throw new Error(
			_.get(parsedPayload, "errorMessage") ||
			_.get(parsedPayload, "message") ||
			"Algolia proxy operation failed."
		);
	}

	return parsedPayload;
}

function createPrivilegedIndex(indexName) {
	return {
		indexName,
		search(query = "", options = {}) {
			return invokeAlgoliaProxy("search", { indexName, query, options });
		},
		getObject(objectID, options = {}) {
			return invokeAlgoliaProxy("getObject", { indexName, objectID, options });
		},
		async getObjects(objectIDs = [], options = {}) {
			if (!Array.isArray(objectIDs)) {
				throw new Error("Algolia object hydration requires an array of object IDs.");
			}
			const requestedObjectIDs = objectIDs;
			const results = [];
			for (let offset = 0; offset < requestedObjectIDs.length; offset += ALGOLIA_PROXY_GET_OBJECTS_CHUNK_SIZE) {
				const chunk = requestedObjectIDs.slice(offset, offset + ALGOLIA_PROXY_GET_OBJECTS_CHUNK_SIZE);
				const response = await invokeAlgoliaProxy("getObjects", {
					indexName,
					objectIDs: chunk,
					attributesToRetrieve: options.attributesToRetrieve
				});
				if (_.get(response, "truncated", false)) {
					throw new Error("Algolia object hydration response was unexpectedly truncated.");
				}
				results.push(...(_.get(response, "results", []) || []));
			}
			return { results };
		},
		getSettings() {
			return invokeAlgoliaProxy("getSettings", { indexName });
		},
		setSettings(settings = {}) {
			return invokeAlgoliaProxy("setSettings", { indexName, settings });
		},
		saveObject(object = {}) {
			return invokeAlgoliaProxy("saveObject", { indexName, object });
		},
		partialUpdateObject(object = {}) {
			return invokeAlgoliaProxy("partialUpdateObject", { indexName, object });
		},
		countUniqueValues(attribute, options = {}) {
			return invokeAlgoliaProxy("countUniqueValues", { indexName, attribute, options });
		},
		async browseObjects(options = {}) {
			const batch = options.batch;
			const requestedPageSize = Number(options.hitsPerPage);
			const pageSize = Number.isFinite(requestedPageSize) && requestedPageSize > 0
				? Math.min(Math.floor(requestedPageSize), ALGOLIA_PROXY_BROWSE_PAGE_SIZE)
				: ALGOLIA_PROXY_BROWSE_PAGE_SIZE;
			const requestOptions = _.assign({}, _.omit(options, ["batch", "cursor"]), {
				hitsPerPage: pageSize
			});
			const allHits = [];
			let cursor = _.toString(options.cursor || "").trim() || null;
			let nbHits = 0;
			const seenCursors = new Set();

			do {
				if (cursor) {
					if (seenCursors.has(cursor)) {
						throw new Error("Algolia browse returned a repeated cursor.");
					}
					seenCursors.add(cursor);
				}
				const result = await invokeAlgoliaProxy("browseObjectsPage", {
					indexName,
					options: requestOptions,
					cursor
				});
				const hits = _.get(result, "hits", []) || [];
				nbHits = Number.isFinite(_.get(result, "nbHits")) ? result.nbHits : nbHits;
				allHits.push(...hits);
				if (_.isFunction(batch)) {
					batch(hits);
				}
				cursor = _.toString(_.get(result, "cursor", "")).trim() || null;
			} while (cursor);

			return {
				hits: allHits,
				nbHits: nbHits || allHits.length
			};
		}
	};
}

export async function refreshChatModal(instanceId) {
	try {
		// Initialize the Algolia index
		const index = getClient2().initIndex(getHistoryIndexName());

		if (getClient2().clearCache) { await getClient2().clearCache(); }

		console.log("Search Index Name", getHistoryIndexName());
		// Perform the search for the specific instanceId
		const results = await index.search("", {
			filters: `instanceId:${instanceId}`,
			hitsPerPage: 1 // Limit to one result since instanceId should be unique
		});
		// Check if a result was found
		if (results.hits && results.hits.length > 0) {
			const hit = results.hits[0];
			state.set(["appState", "showCreateChatChannel", "instance"], hit);

			const chatModalInstanceId = state.get(["appState", "showCreateChatChannel", "instance", "instanceId"]);
			const chatModalChecklist = state.get(["appState", "showCreateChatChannel", "checklist"]);
			const currentChecklist = state.get(["currentChecklist"]);
			if (
				chatModalInstanceId === instanceId
				&& chatModalChecklist
				&& Array.isArray(chatModalChecklist.invoiceItems)
			) {
				state.set(["appState", "activityInvoiceItems"], JSON.parse(JSON.stringify(chatModalChecklist.invoiceItems)));
			} else if (currentChecklist && currentChecklist.instanceId === instanceId && currentChecklist.hasOwnProperty("invoiceItems")) {
				state.set(["appState", "activityInvoiceItems"], JSON.parse(JSON.stringify(currentChecklist.invoiceItems)));
			} else {
				state.set(["appState", "activityInvoiceItems"], []);
			}

			let sessions = {};
			sessions[hit.id] = [hit];

			state.set(["appState", "showCreateChatChannel", "sessions"], sessions);
		} else {
			console.warn(`No data found for instanceId: ${instanceId}`);
		}
	} catch (error) {
		console.error(`Error fetching data for instanceId: ${instanceId}`, error);
		throw error;
	}
}

const DEFAULT_AND_REFINEMENT_LIST_ATTRIBUTES = ["genre", "jobFlags", "tags"];

function normalizeFacetRefinementValues(value, attribute, allowMultiple = true) {
	const values = Array.isArray(value) ? value : (_.isNil(value) || value === "" ? [] : [value]);
	const nonEmptyValues = values.filter(item => !_.isNil(item) && item !== "");

	if (!allowMultiple && nonEmptyValues.length > 1) {
		throw new Error(`Menu facet "${attribute}" contains multiple values.`);
	}
	if (nonEmptyValues.some(item => _.isObject(item))) {
		throw new Error(`Facet "${attribute}" contains an unsupported value.`);
	}

	return _.uniq(nonEmptyValues.map(item => _.toString(item))).sort();
}

function getRefinementListOperator(searchState, attribute) {
	const configuredOperator = _.get(searchState, ["refinementListOperators", attribute]);
	if (configuredOperator === "and" || configuredOperator === "or") {
		return configuredOperator;
	}
	if (!_.isUndefined(configuredOperator) && configuredOperator !== null) {
		throw new Error(`Facet "${attribute}" contains an unsupported operator.`);
	}

	return DEFAULT_AND_REFINEMENT_LIST_ATTRIBUTES.indexOf(attribute) !== -1 ? "and" : "or";
}

/**
 * Rebuild the facet filters represented by an InstantSearch search state.
 *
 * This intentionally walks the active state instead of maintaining a second
 * list of Status attributes. A hand-maintained list can silently omit a newly
 * added UI filter, causing bulk operations to target more records than the UI
 * displays. A non-empty menu refinement takes precedence over a refinement-list
 * value for the same attribute, matching the Basic/Advanced filter modes.
 */
export function transformSearchStateToFacetFilter(searchState = {}) {
	const facetFilters = [];
	const menu = _.isPlainObject(searchState.menu) ? searchState.menu : {};
	const refinementList = _.isPlainObject(searchState.refinementList) ? searchState.refinementList : {};
	const menuRefinedAttributes = new Set();

	Object.keys(menu).sort().forEach(attribute => {
		const values = normalizeFacetRefinementValues(menu[attribute], attribute, false);
		if (values.length === 0) {
			return;
		}

		menuRefinedAttributes.add(attribute);
		facetFilters.push(values.map(value => `${attribute}:${value}`));
	});

	Object.keys(refinementList).sort().forEach(attribute => {
		if (menuRefinedAttributes.has(attribute)) {
			return;
		}

		const values = normalizeFacetRefinementValues(refinementList[attribute], attribute);
		if (values.length === 0) {
			return;
		}

		const filters = values.map(value => `${attribute}:${value}`);
		if (getRefinementListOperator(searchState, attribute) === "and") {
			facetFilters.push(...filters);
		} else {
			facetFilters.push(filters);
		}
	});

	return facetFilters;
}

function transformSearchStateToNumericFilter(searchState) {
	const arrNumericFilters = [];

	const arrDueDateTimes = [];
	const arrCreatedDateTimes = [];
	const arrLastUpdatedDateTimes = [];

	// Due Date
	if (searchState.hasOwnProperty("multiRange") && searchState.multiRange.hasOwnProperty("dueDate") && searchState.multiRange.dueDate !== "") {
		arrDueDateTimes.push("dueDate:" + (searchState.multiRange.dueDate.split(":")[0] ? searchState.multiRange.dueDate.split(":")[0] : "0") + " TO " + searchState.multiRange.dueDate.split(":")[1]);
		arrNumericFilters.push(arrDueDateTimes);
	}

	// Started Timestamp
	if (searchState.hasOwnProperty("multiRange") && searchState.multiRange.hasOwnProperty("createdDateTime") && searchState.multiRange.createdDateTime !== "") {
		arrCreatedDateTimes.push("createdDateTime:" + searchState.multiRange.createdDateTime.split(":")[0] + " TO " + (searchState.multiRange.createdDateTime.split(":")[1] ? searchState.multiRange.createdDateTime.split(":")[1] : (Date.now() / 1000).toFixed(0)));
		arrNumericFilters.push(arrCreatedDateTimes);
	}

	// Last Updated Timestamp
	if (searchState.hasOwnProperty("multiRange") && searchState.multiRange.hasOwnProperty("lastUpdatedDateTime") && searchState.multiRange.lastUpdatedDateTime !== "") {
		arrLastUpdatedDateTimes.push("lastUpdatedDateTime:" + searchState.multiRange.lastUpdatedDateTime.split(":")[0] + " TO " + (searchState.multiRange.lastUpdatedDateTime.split(":")[1] ? searchState.multiRange.lastUpdatedDateTime.split(":")[1] : (Date.now() / 1000).toFixed(0)));
		arrNumericFilters.push(arrLastUpdatedDateTimes);
	}

	return arrNumericFilters;
}

function getHistoryReindexBrowseOptions(searchState = {}, includeAll = false) {
	const options = {
		attributesToRetrieve: ["url"],
		query: includeAll ? "" : (searchState.query || "")
	};

	if (!includeAll) {
		const arrFacetFilters = transformSearchStateToFacetFilter(searchState);
		const arrNumericFilters = transformSearchStateToNumericFilter(searchState);
		if (arrFacetFilters.length > 0) {
			options.facetFilters = arrFacetFilters;
		}
		if (arrNumericFilters.length > 0) {
			options.numericFilters = arrNumericFilters;
		}
	}

	return options;
}

async function browseHistoryFilesForReindex(searchState, includeAll) {
	const hits = [];
	await getHistoryIndex().browseObjects({
		...getHistoryReindexBrowseOptions(searchState, includeAll),
		batch: batch => {
			hits.push(...(batch || []));
		}
	});
	return hits;
}

function getUniqueHistoryFileUrls(hits = []) {
	return _.uniq(hits
		.map(hit => _.toString(hit && hit.url ? hit.url : "").trim())
		.filter(Boolean));
}

async function touchHistoryFileUrls(s3, urls) {
	let nextIndex = 0;
	let touched = 0;
	const failures = [];
	const workerCount = Math.min(HISTORY_REINDEX_CONCURRENCY, urls.length);

	async function touchNext() {
		while (nextIndex < urls.length) {
			const currentIndex = nextIndex;
			nextIndex += 1;
			const url = urls[currentIndex];
			try {
				const sourceObject = await s3.headObject({ Bucket: env.s3.contentBucket, Key: url }).promise();
				const copyParams = {
					MetadataDirective: "REPLACE",
					Metadata: {
						...(sourceObject.Metadata || {}),
						"ambifi-reindexed-at": new Date().toISOString()
					},
					CopySource: `${env.s3.contentBucket}/${encodeS3CopySourceKey(url)}`,
					Bucket: env.s3.contentBucket,
					Key: url
				};
				["CacheControl", "ContentDisposition", "ContentEncoding", "ContentLanguage", "ContentType", "Expires", "WebsiteRedirectLocation"].forEach(property => {
					if (!_.isUndefined(sourceObject[property]) && sourceObject[property] !== null) {
						copyParams[property] = sourceObject[property];
					}
				});
				await s3.copyObject(copyParams).promise();
				touched += 1;
			} catch (err) {
				failures.push({ url, err });
			}
		}
	}

	await Promise.all(Array.from({ length: workerCount }, () => touchNext()));
	return { touched, failures };
}

async function reindexHistoryFiles(searchState = {}, includeAll = false) {
	const title = includeAll ? "Reindex All" : "Touch Filtered Files";
	showStatusSpinner(state);
	try {
		await refreshCredentials();
		const hits = await browseHistoryFilesForReindex(searchState, includeAll);
		const urls = getUniqueHistoryFileUrls(hits);
		const skipped = hits.length - urls.length;
		const result = await touchHistoryFileUrls(new AWS.S3({ useDualStack: true }), urls);
		const summary = `${result.touched} ${result.touched === 1 ? "file" : "files"} touched${skipped > 0 ? `; ${skipped} missing or duplicate URLs skipped` : ""}.`;

		if (result.failures.length > 0) {
			const firstFailure = result.failures[0];
			throw new Error(`${summary} ${result.failures.length} failed. First failure (${firstFailure.url}): ${_.get(firstFailure, "err.message", firstFailure.err)}`);
		}

		showInfo(title, summary);
		return { ...result, skipped };
	} catch (err) {
		console.error(title, err);
		showError2(title, err);
		return null;
	} finally {
		hideStatusSpinner(state);
	}
}

export function touchHistoryFilesFromSearch(searchState) {
	return reindexHistoryFiles(searchState, false);
}

export function reindexAllHistoryFiles() {
	return reindexHistoryFiles({}, true);
}

export async function extractAll(where, orderBy) {
	let selectStmt = "identityId, activityId, instanceId, itemGuid, sessionName, checklistName, checklistCompletionState, checklistCompletionStateString, genre, url, tags, itemId, listIndex, sectionIndex, itemIndex, SUBSTRING(label1, 1, 255) as label1, SUBSTRING(label2, 1, 255) as label2, type, action, timestamp, value, duration ";


	const sql = "" +
		"SELECT " +
		"`ambifi-core`.`history`.`identityId` AS `identityId`, " +
		"`ambifi`.`OrganizationMember`.`displayName` AS `displayName`, " +
		"`ambifi`.`OrganizationMember`.`inviteEmail` AS `email`, " +
		((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_1` AS `id34`, " : "") +
		((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_2` AS `coid`, " : "") +
		((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_4` AS `facility`, " : "") +
		((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_5` AS `division`, " : "") +
		((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_6` AS `department`, " : "") +
		((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_8` AS `jobtitle`, " : "") +
		"`ambifi-core`.`history`.`activityId` AS `activityId`, " +
		"`ambifi-core`.`history`.`instanceId` AS `instanceId`, " +
		"`ambifi`.`InstanceJobId`.`jobId` AS `jobId`, " +
		"`ambifi`.`InstanceJobId`.`projectId` AS `projectId`, " +
		"`ambifi-core`.`history`.`sessionName` AS `sessionName`, " +
		"`ambifi-core`.`history`.`checklistName` AS `checklistName`, " +
		"`ambifi-core`.`history`.`checklistDescription` AS `checklistDescription`, " +
		"`ambifi-core`.`history`.`checklistCompletionState` AS `checklistCompletionState`, " +
		"`ambifi-core`.`history`.`checklistCompletionStateString` AS `checklistCompletionStateString`, " +
		"`ambifi-core`.`history`.`activityResult` AS `activityResult`, " +
		"`ambifi-core`.`history`.`genre` AS `genre`, " +
		"`ambifi-core`.`history`.`url` AS `url`, " +
		"`ambifi-core`.`history`.`tags` AS `tags`, " +
		"`ambifi-core`.`history`.`itemGuid` AS `itemGuid`, " +
		"`ambifi-core`.`history`.`itemId` AS `itemId`, " +
		"`ambifi-core`.`history`.`listIndex` AS `listIndex`, " +
		"`ambifi-core`.`history`.`sectionIndex` AS `sectionIndex`, " +
		"`ambifi-core`.`history`.`itemIndex` AS `itemIndex`, " +
		"`ambifi-core`.`history`.`label1` AS `label1`, " +
		"`ambifi-core`.`history`.`label2` AS `label2`, " +
		"`ambifi-core`.`history`.`type` AS `type`, " +
		"`ambifi-core`.`history`.`action` AS `action`, " +
		"`ambifi-core`.`history`.`value` AS `value`, " +
		"`ambifi-core`.`history`.`timestamp` AS `timestamp`, " +
		"`ambifi-core`.`history`.`duration` AS `duration` " +
		"FROM " +
		"(`ambifi-core`.`history` JOIN `ambifi`.`OrganizationMember` ON `ambifi-core`.`history`.`identityId`=`ambifi`.`OrganizationMember`.`userSubId` LEFT OUTER JOIN `ambifi`.`OrganizationMemberInfo` ON `ambifi-core`.`history`.`identityId`=`ambifi`.`OrganizationMemberInfo`.`userSubId` LEFT OUTER JOIN `ambifi`.`InstanceJobId` ON `ambifi-core`.`history`.`orgId`=`ambifi`.`InstanceJobId`.`orgId` AND `ambifi-core`.`history`.`instanceId`=`ambifi`.`InstanceJobId`.`instanceId`) " +
		"WHERE " +
		"(`ambifi-core`.`history`.`orgId` = '" + getOrgId() + "') AND (`ambifi-core`.`history`.`deleted` = 0) AND action <> 'setSketchPad' AND action <> 'setImagePicker' " + ((where && where !== "") ? " AND " + where + " " : "") +
		"ORDER BY " +
		((orderBy && orderBy !== "") ? orderBy : "identityId, activityId, instanceId, timestamp");

	console.log(sql);

	let fields;
	if (getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") {
		fields = ["identityId", "displayName", "email", "id34", "coid", "facility", "division", "department", "jobtitle", "activityId", "instanceId", "jobId", "projectId", "sessionName", "checklistName", "checklistDescription", "checklistCompletionState", "checklistCompletionStateString", "activityResult", "genre", "url", "tags", "itemGuid", "itemId", "listIndex", "sectionIndex", "itemIndex", "label1", "label2", "type", "action", "value", "timestamp", "duration"];
	} else {
		fields = ["identityId", "displayName", "email", "activityId", "instanceId", "jobId", "projectId", "sessionName", "checklistName", "checklistDescription", "checklistCompletionState", "checklistCompletionStateString", "activityResult", "genre", "url", "tags", "itemGuid", "itemId", "listIndex", "sectionIndex", "itemIndex", "label1", "label2", "type", "action", "value", "timestamp", "duration"];
	}

	awsS3.getExtract(sql, fields, "history-extract-");

	/*
	
	
	
		const browser = index.browseAll();
		let hits = [];
	
		browser.on("result", function onResult(content) {
			hits = hits.concat(content.hits);
		});
	
		browser.on("end", function onEnd() {
			console.log("Finished!");
			console.log("We got %d hits", hits.length);
	
			// Need to get each S3 doc in batch to get history
		});
	
		browser.on("error", function onError(err) {
			throw err;
		});
	*/
}

export async function extractAllSessions(where, orderBy) {
	// To make completely dynmic, could query OrganizationMemberInfo table first for Org to get the types that need returned

	const sql = "" +
		"SELECT " +
		"`ambifi-core`.`history`.`identityId` AS `identityId`, " +
		"`ambifi`.`OrganizationMember`.`displayName` AS `displayName`, " +
		"`ambifi`.`OrganizationMember`.`inviteEmail` AS `email`, " +
		((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_1` AS `id34`, " : "") +
		((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_2` AS `coid`, " : "") +
		((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_4` AS `facility`, " : "") +
		((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_5` AS `division`, " : "") +
		((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_6` AS `department`, " : "") +
		((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_8` AS `jobtitle`, " : "") +
		"`ambifi-core`.`history`.`activityId` AS `activityId`, " +
		"`ambifi-core`.`history`.`instanceId` AS `instanceId`, " +
		"`ambifi`.`InstanceJobId`.`jobId` AS `jobId`, " +
		"`ambifi`.`InstanceJobId`.`projectId` AS `projectId`, " +
		"`ambifi-core`.`history`.`sessionName` AS `sessionName`, " +
		"`ambifi-core`.`history`.`checklistName` AS `checklistName`, " +
		"`ambifi-core`.`history`.`checklistDescription` AS `checklistDescription`, " +
		"`ambifi-core`.`history`.`checklistCompletionState` AS `checklistCompletionState`, " +
		"`ambifi-core`.`history`.`checklistCompletionStateString` AS `checklistCompletionStateString`, " +
		"`ambifi-core`.`history`.`activityResult` AS `activityResult`, " +
		"`ambifi-core`.`history`.`genre` AS `genre`, " +
		"`ambifi-core`.`history`.`tags` AS `tags`, " +
		"`ambifi-core`.`history`.`url` AS `url`, " +
		"`ambifi-core`.`history`.`timestamp` AS `timestamp`, " +
		"SUM(duration) as 'totalDuration', " +
		"AVG(duration) as 'avgPerItem' " +
		"FROM " +
		"(`ambifi-core`.`history` JOIN `ambifi`.`OrganizationMember` ON `ambifi-core`.`history`.`identityId`=`ambifi`.`OrganizationMember`.`userSubId` LEFT OUTER JOIN `ambifi`.`OrganizationMemberInfo` ON `ambifi-core`.`history`.`identityId`=`ambifi`.`OrganizationMemberInfo`.`userSubId` LEFT OUTER JOIN `ambifi`.`InstanceJobId` ON `ambifi-core`.`history`.`orgId`=`ambifi`.`InstanceJobId`.`orgId` AND `ambifi-core`.`history`.`instanceId`=`ambifi`.`InstanceJobId`.`instanceId`) " +
		"WHERE " +
		"((`ambifi-core`.`history`.`orgId` = '" + getOrgId() + "') " +
		" AND (`ambifi-core`.`history`.`deleted` = 0) " + ((where && where !== "") ? " AND " + where + " " : "") +
		") " +
		"GROUP BY `ambifi-core`.`history`.`instanceId` " +
		"ORDER BY " +
		((orderBy && orderBy !== "") ? orderBy : "`ambifi-core`.`history`.`timestamp` ASC");


	console.log(sql);

	let fields;

	if (getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") {
		fields = ["identityId", "displayName", "email", "id34", "coid", "facility", "division", "department", "jobtitle", "activityId", "instanceId", "jobId", "projectId", "sessionName", "checklistName", "checklistDescription", "checklistCompletionState", "checklistCompletionStateString", "activityResult", "genre", "tags", "url", "timestamp", "totalDuration", "avgPerItem"];
	} else {
		fields = ["identityId", "displayName", "email", "activityId", "instanceId", "jobId", "projectId", "sessionName", "checklistName", "checklistDescription", "checklistCompletionState", "checklistCompletionStateString", "activityResult", "genre", "tags", "url", "timestamp", "totalDuration", "avgPerItem"];
	}

	awsS3.getExtract(sql, fields, "session-extract-");
}

export async function countUniqueUsers(searchState, showHealthStream) {
	try {
		let index = getHistoryIndex();

		const arrFacetFilters = transformSearchStateToFacetFilter(searchState);
		const arrNumericFilters = transformSearchStateToNumericFilter(searchState);

		console.log("SEARCH STATE", searchState, arrFacetFilters, arrNumericFilters);

		let options = {
		};

		if (!showHealthStream) {
			options.filters = "NOT deliveryPlatform:HealthStream";
		}

		if (arrFacetFilters.length > 0) {
			options.facetFilters = arrFacetFilters;
		}
		if (arrNumericFilters.length > 0) {
			options.numericFilters = arrNumericFilters;
		}

		showStatusSpinner(state);

		const result = await index.countUniqueValues("displayName", {
			query: "",
			// filters: `createdDateTime>1690804800 AND createdDateTime<1693569600`,
			...options
		});

		const totalUsers = result && _.isNumber(result.count) ? result.count : 0;

		hideStatusSpinner(state);

		showInfo("Unique Users", "" + totalUsers);

		console.log("UNIQUE USERS", totalUsers, result);

		return totalUsers;
	} catch (err) {
		console.error("ERROR", err);
		hideStatusSpinner(state);
		showError2("Count Unique Users", err);
	}
}

export async function getInvoicesByIdentityId(identityId, startDate, endDate) {
	try {
		let index = getHistoryIndex();

		const arrFacetFilters = ["invoiceItems.identityId:" + identityId];
		// const arrNumericFilters = transformSearchStateToNumericFilter(searchState);

		// console.log("SEARCH STATE", searchState, arrFacetFilters, arrNumericFilters);

		let options = {
		};

		if (arrFacetFilters.length > 0) {
			options.facetFilters = arrFacetFilters;
		}
		// if (arrNumericFilters.length > 0) {
		// 	options.numericFilters = arrNumericFilters
		// }

		showStatusSpinner(state);

		let hits = [];
		await index.browseObjects({
			query: "",
			// attributesToRetrieve: "displayName",
			// filters: `createdDateTime>1690804800 AND createdDateTime<1693569600`,
			...options,
			batch: batch => {
				hits = hits.concat(batch);
			}
		});

		// console.log("HITS", hits.length, hits[0]);

		const invoiceItems = [];
		for (let i = 0; i < hits.length; i++) {
			invoiceItems.push(hits[i].invoiceItems);
		}

		hideStatusSpinner(state);

		return invoiceItems;
	} catch (err) {
		console.error("ERROR", err);
	}
}

export async function getSearchParamsFromSearchState(searchState, currentDateFilters) {
	return new Promise(async (resolve, reject) => {
		refreshCredentials().then(({ cognitoUser }) => {
			console.log(searchState);

			const arrFacetFilters = transformSearchStateToFacetFilter(searchState);
			const arrNumericFilters = transformSearchStateToNumericFilter(searchState);

			let options = {
				hitsPerPage: 1000
			};

			if (arrFacetFilters.length > 0) {
				options.facetFilters = arrFacetFilters;
			}
			if (arrNumericFilters.length > 0) {
				options.numericFilters = arrNumericFilters;
			}

			resolve({
				query: searchState.query ? searchState.query : "",
				options: options,
				currentDateFilters
			});
		}).catch((err) => {
			console.error(err);
			showError2("getSearchParamsFromSearchState", err);
			reject(err);
		});
	});
}



export async function extractAllFiltered(searchState) {
	refreshCredentials().then(async ({ cognitoUser }) => {
		console.log(searchState);

		let index = getClient2().initIndex(getHistoryIndexName());

		const arrFacetFilters = transformSearchStateToFacetFilter(searchState);
		const arrNumericFilters = transformSearchStateToNumericFilter(searchState);

		let options = {
			hitsPerPage: 1000
		};

		if (arrFacetFilters.length > 0) {
			options.facetFilters = arrFacetFilters;
		}
		if (arrNumericFilters.length > 0) {
			options.numericFilters = arrNumericFilters;
		}

		let results = await index.search(searchState.hasOwnProperty("query") ? searchState.query : "", options);
		//let results = await index.search('', options);

		console.log(results);

		let hits = results.hits;

		let instancesFilter = "";

		let instances = "";
		for (let i = 0; i < hits.length; i++) {
			const hit = hits[i];

			if (i === 0) {
				instances = "'" + hit.instanceId + "'";
			} else {
				instances = instances + ", '" + hit.instanceId + "'";
			}
		}

		if (instances !== "") {
			instancesFilter = " AND instanceId IN (" + instances + ")";
		}

		const sql = "" +
			"SELECT " +
			"`ambifi-core`.`history`.`identityId` AS `identityId`, " +
			"`ambifi`.`OrganizationMember`.`displayName` AS `displayName`, " +
			"`ambifi`.`OrganizationMember`.`inviteEmail` AS `email`, " +
			((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_1` AS `id34`, " : "") +
			((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_2` AS `coid`, " : "") +
			((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_4` AS `facility`, " : "") +
			((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_5` AS `division`, " : "") +
			((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_6` AS `department`, " : "") +
			((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_8` AS `jobtitle`, " : "") +
			"`ambifi-core`.`history`.`activityId` AS `activityId`, " +
			"`ambifi-core`.`history`.`instanceId` AS `instanceId`, " +
			"`ambifi`.`InstanceJobId`.`jobId` AS `jobId`, " +
			"`ambifi`.`InstanceJobId`.`projectId` AS `projectId`, " +
			"`ambifi-core`.`history`.`sessionName` AS `sessionName`, " +
			"`ambifi-core`.`history`.`checklistName` AS `checklistName`, " +
			"`ambifi-core`.`history`.`checklistDescription` AS `checklistDescription`, " +
			"`ambifi-core`.`history`.`checklistCompletionState` AS `checklistCompletionState`, " +
			"`ambifi-core`.`history`.`checklistCompletionStateString` AS `checklistCompletionStateString`, " +
			"`ambifi-core`.`history`.`activityResult` AS `activityResult`, " +
			"`ambifi-core`.`history`.`genre` AS `genre`, " +
			"`ambifi-core`.`history`.`url` AS `url`, " +
			"`ambifi-core`.`history`.`tags` AS `tags`, " +
			"`ambifi-core`.`history`.`itemGuid` AS `itemGuid`, " +
			"`ambifi-core`.`history`.`itemId` AS `itemId`, " +
			"`ambifi-core`.`history`.`listIndex` AS `listIndex`, " +
			"`ambifi-core`.`history`.`sectionIndex` AS `sectionIndex`, " +
			"`ambifi-core`.`history`.`itemIndex` AS `itemIndex`, " +
			"`ambifi-core`.`history`.`label1` AS `label1`, " +
			"`ambifi-core`.`history`.`label2` AS `label2`, " +
			"`ambifi-core`.`history`.`type` AS `type`, " +
			"`ambifi-core`.`history`.`action` AS `action`, " +
			"`ambifi-core`.`history`.`value` AS `value`, " +
			"`ambifi-core`.`history`.`timestamp` AS `timestamp`, " +
			"`ambifi-core`.`history`.`duration` AS `duration` " +
			"FROM " +
			"(`ambifi-core`.`history` JOIN `ambifi`.`OrganizationMember` ON `ambifi-core`.`history`.`identityId`=`ambifi`.`OrganizationMember`.`userSubId` LEFT OUTER JOIN `ambifi`.`OrganizationMemberInfo` ON `ambifi-core`.`history`.`identityId`=`ambifi`.`OrganizationMemberInfo`.`userSubId` LEFT OUTER JOIN `ambifi`.`InstanceJobId` ON `ambifi-core`.`history`.`orgId`=`ambifi`.`InstanceJobId`.`orgId` AND `ambifi-core`.`history`.`instanceId`=`ambifi`.`InstanceJobId`.`instanceId`) " +
			"WHERE " +
			"(`ambifi-core`.`history`.`orgId` = '" + getOrgId() + "')" + instancesFilter + " AND action <> 'setSketchPad' AND action <> 'setImagePicker' " +
			"ORDER BY " +
			"identityId, activityId, instanceId, timestamp";

		console.log(sql);

		let fields;
		if (getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") {
			fields = ["identityId", "displayName", "email", "id34", "coid", "facility", "division", "department", "jobtitle", "activityId", "instanceId", "jobId", "projectId", "sessionName", "checklistName", "checklistDescription", "checklistCompletionState", "checklistCompletionStateString", "activityResult", "genre", "url", "tags", "itemGuid", "itemId", "listIndex", "sectionIndex", "itemIndex", "label1", "label2", "type", "action", "value", "timestamp", "duration"];
		} else {
			fields = ["identityId", "displayName", "email", "activityId", "instanceId", "jobId", "projectId", "sessionName", "checklistName", "checklistDescription", "checklistCompletionState", "checklistCompletionStateString", "activityResult", "genre", "url", "tags", "itemGuid", "itemId", "listIndex", "sectionIndex", "itemIndex", "label1", "label2", "type", "action", "value", "timestamp", "duration"];
		}

		if (results.nbHits > 1000) {
			window.confirm("When doing an extract from search results, there is a limit of 1000 sessions that can be returned. The current search results has " + results.nbHits + " records. You can apply more filters to reduce the result size.\n\nDo you want to continue generating the extract with the first 1000 sessions?", () => {
				awsS3.getExtract(sql, fields, "history-extract-search-results-");
			});
		} else {
			window.confirm("This will generate an extract with " + results.nbHits + " sessions.\n\nDo you want to continue generating the extract?", () => {
				awsS3.getExtract(sql, fields, "history-extract-search-results-");
			});
		}
	}).catch((err) => {
		console.error(err);
		showError2("extractAllFiltered", err);
	});


	/*
	
	
	
		const browser = index.browseAll();
		let hits = [];
	
		browser.on("result", function onResult(content) {
			hits = hits.concat(content.hits);
		});
	
		browser.on("end", function onEnd() {
			console.log("Finished!");
			console.log("We got %d hits", hits.length);
	
			// Need to get each S3 doc in batch to get history
		});
	
		browser.on("error", function onError(err) {
			throw err;
		});
	*/
}


export async function extractAllSessionsFiltered(searchState) {
	refreshCredentials().then(async ({ cognitoUser }) => {
		// To make completely dynmic, could query OrganizationMemberInfo table first for Org to get the types that need returned
		console.log(searchState);

		let index = getClient2().initIndex(getHistoryIndexName());

		const arrFacetFilters = transformSearchStateToFacetFilter(searchState);
		const arrNumericFilters = transformSearchStateToNumericFilter(searchState);

		let options = {
			hitsPerPage: 1000
		};

		if (arrFacetFilters.length > 0) {
			options.facetFilters = arrFacetFilters;
		}
		if (arrNumericFilters.length > 0) {
			options.numericFilters = arrNumericFilters;
		}

		let results = await index.search(searchState.hasOwnProperty("query") ? searchState.query : "", options);
		//let results = await index.search('', options);

		console.log(results);

		let hits = results.hits;

		let instancesFilter = "";

		let instances = "";
		for (let i = 0; i < hits.length; i++) {
			const hit = hits[i];

			if (i === 0) {
				instances = "'" + hit.instanceId + "'";
			} else {
				instances = instances + ", '" + hit.instanceId + "'";
			}
		}

		if (instances !== "") {
			instancesFilter = " AND instanceId IN (" + instances + ")";
		}

		const sql = "" +
			"SELECT " +
			"`ambifi-core`.`history`.`identityId` AS `identityId`, " +
			"`ambifi`.`OrganizationMember`.`displayName` AS `displayName`, " +
			"`ambifi`.`OrganizationMember`.`inviteEmail` AS `email`, " +
			((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_1` AS `id34`, " : "") +
			((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_2` AS `coid`, " : "") +
			((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_4` AS `facility`, " : "") +
			((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_5` AS `division`, " : "") +
			((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_6` AS `department`, " : "") +
			((getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") ? "`ambifi`.`OrganizationMemberInfo`.`memberInfoValue_8` AS `jobtitle`, " : "") +
			"`ambifi-core`.`history`.`activityId` AS `activityId`, " +
			"`ambifi-core`.`history`.`instanceId` AS `instanceId`, " +
			"`ambifi`.`InstanceJobId`.`jobId` AS `jobId`, " +
			"`ambifi`.`InstanceJobId`.`projectId` AS `projectId`, " +
			"`ambifi-core`.`history`.`sessionName` AS `sessionName`, " +
			"`ambifi-core`.`history`.`checklistName` AS `checklistName`, " +
			"`ambifi-core`.`history`.`checklistDescription` AS `checklistDescription`, " +
			"`ambifi-core`.`history`.`checklistCompletionState` AS `checklistCompletionState`, " +
			"`ambifi-core`.`history`.`checklistCompletionStateString` AS `checklistCompletionStateString`, " +
			"`ambifi-core`.`history`.`activityResult` AS `activityResult`, " +
			"`ambifi-core`.`history`.`genre` AS `genre`, " +
			"`ambifi-core`.`history`.`tags` AS `tags`, " +
			"`ambifi-core`.`history`.`url` AS `url`, " +
			"`ambifi-core`.`history`.`timestamp` AS `timestamp`, " +
			"SUM(duration) as 'totalDuration', " +
			"AVG(duration) as 'avgPerItem' " +
			"FROM " +
			"(`ambifi-core`.`history` JOIN `ambifi`.`OrganizationMember` ON `ambifi-core`.`history`.`identityId`=`ambifi`.`OrganizationMember`.`userSubId` LEFT OUTER JOIN `ambifi`.`OrganizationMemberInfo` ON `ambifi-core`.`history`.`identityId`=`ambifi`.`OrganizationMemberInfo`.`userSubId` LEFT OUTER JOIN `ambifi`.`InstanceJobId` ON `ambifi-core`.`history`.`orgId`=`ambifi`.`InstanceJobId`.`orgId` AND `ambifi-core`.`history`.`instanceId`=`ambifi`.`InstanceJobId`.`instanceId`) " +
			"WHERE " +
			"(`ambifi-core`.`history`.`orgId` = '" + getOrgId() + "')" + instancesFilter +
			"GROUP BY `ambifi-core`.`history`.`instanceId` " +
			"ORDER BY " +
			"`ambifi-core`.`history`.`timestamp` ASC";


		console.log(sql);

		let fields;

		if (getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") {
			fields = ["identityId", "displayName", "email", "id34", "coid", "facility", "division", "department", "jobtitle", "activityId", "instanceId", "jobId", "projectId", "sessionName", "checklistName", "checklistDescription", "checklistCompletionState", "checklistCompletionStateString", "activityResult", "genre", "tags", "url", "timestamp", "totalDuration", "avgPerItem"];
		} else {
			fields = ["identityId", "displayName", "email", "activityId", "instanceId", "jobId", "projectId", "sessionName", "checklistName", "checklistDescription", "checklistCompletionState", "checklistCompletionStateString", "activityResult", "genre", "tags", "url", "timestamp", "totalDuration", "avgPerItem"];
		}

		if (results.nbHits > 1000) {
			window.confirm("When doing an extract from search results, there is a limit of 1000 sessions that can be returned. The current search results has " + results.nbHits + " records. You can apply more filters to reduce the result size.\n\nDo you want to continue generating the extract with the first 1000 sessions?", () => {
				awsS3.getExtract(sql, fields, "session-extract-search-results-");
			});
		} else {
			window.confirm("This will generate an extract with " + results.nbHits + " sessions.\n\nDo you want to continue generating the extract?", () => {
				awsS3.getExtract(sql, fields, "session-extract-search-results-");
			});
		}
	}).catch((err) => {
		console.error(err);
		showError2("extractAllSessionsFiltered", err);
	});
}



// Do I want to directly set state here, or control all through actions?
export function searchFeatured(text) {
	let index = getClient().initIndex(env.algolia.index);
	index.search(text, { hitsPerPage: 10 }, function (err, content) {
		state.set(["search", "featured"], content.hits);
	});
}

export function searchFeatured2() {
	let index = getClient().initIndex(env.algolia.index);

	let hitsFeatured = [];
	let hitsFeaturedSims = [];
	let hitsFeaturedSnippets = [];

	index.search("self", { hitsPerPage: 100, restrictSearchableAttributes: "publisher" }, function (err, content) {
		if (err) {
			console.error("Connection to Algolia failed", err);
			alert("Error connecting to Algolia.");
			state.set(["search", "featured"], []);
			state.set(["search", "featuredSnippets"], []);
			state.set(["search", "featuredSims"], []);
			return;
		}

		if (content === null) {
			state.set(["search", "featured"], []);
			state.set(["search", "featuredSnippets"], []);
			state.set(["search", "featuredSims"], []);

			return;
		}
		let hits = content.hits;

		hits.sort(compare);

		for (let i = 0; i < hits.length; i++) {
			let foundSim = false;
			let foundSnippet = false;

			let hit = hits[i];

			if (hit.tags.length > 0) {
				for (let j = 0; j < hit.tags.length; j++) {
					let tag = hit.tags[j];
					if (tag.toLowerCase() === "simulation") {
						foundSim = true;
					} else if (tag.toLowerCase() === "snippet") {
						foundSnippet = true;
					}
				}
			}

			if (foundSnippet) {
				hitsFeaturedSnippets.push(hit);
			} else if (foundSim) {
				hitsFeaturedSims.push(hit);
			} else {
				hitsFeatured.push(hit);
			}
		}
		state.set(["search", "featured"], hitsFeatured);
		state.set(["search", "featuredSnippets"], hitsFeaturedSnippets);
		state.set(["search", "featuredSims"], hitsFeaturedSims);
	});
}

function compare(a, b) {
	if (a.name.trim() < b.name.trim()) { return -1; }
	if (a.name.trim() > b.name.trim()) { return 1; }
	return 0;
}

export function searchFeatured3() {
	let index = getClient().initIndex(env.algolia.index);
	index.search("self", {
		"getRankingInfo": 1,
		"facets": "*",
		"attributesToRetrieve": "*",
		"highlightPreTag": "<em>",
		"highlightPostTag": "</em>",
		"hitsPerPage": 10,
		"restrictSearchableAttributes": "publisher",
		"facetFilters": [],
		"maxValuesPerFacet": 100
	}, function (err, content) {
		state.set(["search", "featured"], content.hits);
	});
}


export function searchCheckMate() {
	let index = getClient().initIndex(env.algolia.index);
	index.search("CheckMate", { hitsPerPage: 100 }, function (err, content) {
		if (err) {
			console.error("Connection to Algolia failed", err);
			alert("Error connecting to Algolia.");
			state.set(["search", "checkmate"], []);
			return;
		}
		if (content === null) {
			state.set(["search", "checkmate"], []);
			return;
		}

		let hits = content.hits;
		hits.sort(compare);
		state.set(["search", "checkmate"], hits);
	});
}

export function searchTrending(text) {
	let index = getClient().initIndex(env.algolia.index);
	index.search(text, { hitsPerPage: 10 }, function (err, content) {
		state.set(["search", "trending"], content.hits);
	});
}

export function searchRecommended(text) {
	let index = getClient().initIndex(env.algolia.index);
	index.search(text, { hitsPerPage: 10 }, function (err, content) {
		state.set(["search", "recommended"], content.hits);
	});
}

export function searchOrgActivities() {

	return new Promise(async (resolve, reject) => {
		try {
			const index = getClient2().initIndex(getSearchIndexName());

			let hits = [];
			hits = await index.search("");
			resolve(hits.nbHits);

			// Wasn't working
			// index.browse({
			// 	query: "",
			// 	attributesToRetrieve: "*"
			// },
			// 	function searchDone(err, content) {
			// 		debugger;
			// 		if (err) {
			// 			reject(err);
			// 		} else {
			// 			resolve(content);
			// 		}
			// 	});
		} catch (e) {
			console.error(e);
			reject(e);
		}

	});
}

export function getSearchIndex() {
	const index = createPrivilegedIndex(getSearchIndexName());

	return index;
}

export function getHistoryIndex() {
	const index = createPrivilegedIndex(getHistoryIndexName());

	return index;
}

export async function hydrateHistorySelectionRefs(refs = [], attributesToRetrieve) {
	const normalizedRefs = normalizeStatusSelectionRefs(refs);
	if (normalizedRefs.length === 0) {
		return [];
	}

	const objectIDs = normalizedRefs.map(ref => ref.objectID || ref.instanceId);
	const options = {};
	if (Array.isArray(attributesToRetrieve)) {
		options.attributesToRetrieve = _.uniq(["objectID", "instanceId", "url"].concat(attributesToRetrieve));
	}
	const response = await getHistoryIndex().getObjects(objectIDs, options);
	const rawResults = _.get(response, "results", []) || [];
	const missingRefs = normalizedRefs.filter((_ref, index) => !rawResults[index]);
	if (missingRefs.length > 0) {
		const firstMissingId = missingRefs[0].instanceId;
		throw new Error(`Could not load ${missingRefs.length} selected ${missingRefs.length === 1 ? "record" : "records"} from search. First missing instance: ${firstMissingId}. Refresh the Status page and try again.`);
	}

	const results = rawResults.map((result, index) => ({
		...result,
		instanceId: normalizedRefs[index].instanceId
	}));
	return mergeStatusSelectionData(normalizedRefs, results);
}

export async function initOrgIndex() {
	// Org index settings are managed by backend/admin flows. App boot should not
	// require a write key or block on Lambda.
	return Promise.resolve();
}

export async function initHistoryIndex() {
	// JJB - Shouldn't need anymore
	// await initializeHistoryIndex(index);
}

export async function updateDueDate(objectID, dueDate, dueDateIsoString) {
	let index = getHistoryIndex();

	const updateObject = {
		objectID,
		dueDate,
		dueDateIsoString
	};

	await index.partialUpdateObject(updateObject);
}

function getUserHistoryDisplayName() {
	const user = state.select(["user"]).get();
	return _.trim(
		_.get(user, "displayName") ||
		_.get(user, "name") ||
		_.get(state.get(["selectedOrg"]), "member.displayName") ||
		""
	);
}

export async function searchUserActivities() {
	console.log("searchUserActivities");
	try {
		console.log("searchUserActivities (2)");
		const index = getClient2().initIndex(getHistoryIndexName());

		console.log("searchUserActivities (3)");
		const user = state.select(["user"]).get();
		const displayName = getUserHistoryDisplayName();

		if (_.isUndefined(user)) {
			return {};
		}

		if (displayName === "") {
			return { hits: [] };
		}

		const searchHits = async () => {
			let hits = [];
			let page = 0;
			let response = null;
			do {
				response = await index.search("", {
					attributesToRetrieve: ["*"],
					filters: `displayName:${JSON.stringify(displayName)}`,
					hitsPerPage: 1000,
					page
				});
				hits = hits.concat(response.hits || []);
				page++;
			} while (response && page < response.nbPages);
			return hits;
		};

		console.log("searchUserActivities (5)");
		try {
			const hits = await searchHits();
			console.log("searchUserActivities (6)", hits);
			return { hits };
		} catch (err) {
			if (err.statusCode !== 404) {
				throw err;
			}

			await new Promise(resolve => setTimeout(resolve, 2000));
			console.log("searchUserActivities (7)");
			const hits = await searchHits();
			console.log("searchUserActivities (8)", hits);
			return { hits };
		}
	} catch (e) {
		console.error("searchUserActivities (x1)", e);
		throw e;
	}
}
